import { Checkbox, Modal, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import { isCrossAccountSellingEnabled } from "common/src/models/Seller";
import React, { useState } from "react";
import { Affiliate, XolaPartner, XolaPartnerStatus } from "types";
import { useAutoFetchCollection } from "../../hooks/useAutoFetchCollection";
import { SelectionState, useSelection } from "../../hooks/useSelection";
import { InfiniteList } from "./InfiniteList";
import { NoTranslate } from "./NoTranslate";
import { SearchInput } from "./SearchInput";
import { SecondaryButton } from "./SecondaryButton";
import { useSeller } from "@/hooks/useAccount";
import { useSearch } from "@/hooks/useSearch";

interface PartnerSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    value?: PartnerSelectionState;
    onChange: (value: PartnerSelectionState) => void;
    title: string;
}

export interface PartnerSelectionState {
    agents: SelectionState;
    operators: SelectionState;
}

export const PartnerSelectorModal = ({ isOpen, onClose, value, onChange, title }: PartnerSelectorModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent value={value} title={title} onChange={onChange} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps extends Omit<PartnerSelectorModalProps, "isOpen"> {}

const ModalContent = ({ onClose, value, onChange, title }: ModalContentProps) => {
    const seller = useSeller();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const shouldShowOperators = isCrossAccountSellingEnabled(seller);
    const {
        data: agents,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useAutoFetchCollection<Affiliate>("affiliates");

    const {
        data: operators,
        hasNextPage: hasNextPageOperators,
        fetchNextPage: fetchNextPageOperators,
        isFetchingNextPage: isFetchingNextPageOperators,
    } = useAutoFetchCollection<XolaPartner>(
        "partners",
        { expand: "partnerSeller", status: XolaPartnerStatus.ACCEPTED, seller: seller.id },
        { enabled: shouldShowOperators },
    );

    const selection = useSelection(agents, hasNextPage ?? false, value?.agents);
    const operatorSelection = useSelection(
        operators,
        hasNextPageOperators ?? false,
        getPartnerStateFromPartnerSellerState(operators, value?.operators),
    );

    const filteredAgents = useSearch(agents, (agent) => [agent.name], searchTerm || "");
    const filteredOperators = useSearch(operators, (operator) => [operator.partnerSeller?.name], searchTerm || "");

    const handleConfirmClick = () => {
        const operatorState: SelectionState = {
            all: operatorSelection.state.all,
            selected: getSelectedPartnerSellersFromPartners(operators, operatorSelection.state.selected),
        };
        const value = {
            agents: selection.state,
            operators: operatorState,
        };
        onChange(value);
        onClose();
    };

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult);
    };

    const checked = searchTerm
        ? isFilteredSelectionChecked(filteredOperators, operatorSelection.state.storedIds) &&
          isFilteredSelectionChecked(filteredAgents, selection.state.storedIds)
        : selection.isSelectedAll && operatorSelection.isSelectedAll;

    return (
        <>
            <Modal.Header>{title}</Modal.Header>

            <Modal.Body>
                <div className="overflow-hidden rounded-lg border border-gray-lighter">
                    <div className="px-2 py-3">
                        <SearchInput onChange={handleSearchChange} />
                    </div>
                    <div className="flex items-center justify-between bg-gray-lighter p-3">
                        {filteredAgents.length + filteredOperators.length > 0 && (
                            <Checkbox
                                checked={checked}
                                label="Select All"
                                classNames={{
                                    label: "font-bold text-gray-darker",
                                }}
                                onChange={() => {
                                    if (searchTerm) {
                                        if (checked) {
                                            if (filteredAgents.length > 0) {
                                                selection.deselectFromSelection(filteredAgents);
                                            }

                                            if (filteredOperators.length > 0) {
                                                operatorSelection.deselectFromSelection(filteredOperators);
                                            }
                                        } else {
                                            if (filteredAgents.length > 0) {
                                                selection.addToSelection(filteredAgents);
                                            }

                                            if (filteredOperators.length > 0) {
                                                operatorSelection.addToSelection(filteredOperators);
                                            }
                                        }

                                        return;
                                    }

                                    const isAllSelected =
                                        selection.state.all &&
                                        operatorSelection.state.all &&
                                        selection.state.selected.length === 0 &&
                                        operatorSelection.state.selected.length === 0;

                                    const toggleSelection = !isAllSelected;
                                    selection.selectAll(toggleSelection);
                                    if (shouldShowOperators) {
                                        operatorSelection.selectAll(toggleSelection);
                                    }
                                }}
                            />
                        )}

                        <span className="ml-auto text-sm font-bold text-primary">
                            <var data-var="selectionCount">
                                {getSelectionCount(agents, operators, selection.state, operatorSelection.state)}
                            </var>{" "}
                            items selected
                        </span>
                    </div>

                    <div className="p-3">
                        <InfiniteList
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                            hasMore={hasNextPage || hasNextPageOperators}
                            isLoading={isFetchingNextPage || isFetchingNextPageOperators}
                            className="h-60 divide-y divide-gray-light overflow-y-auto overflow-x-hidden rounded border border-gray-light"
                            onLoadMore={() => {
                                fetchNextPage();
                                fetchNextPageOperators();
                            }}
                        >
                            {filteredAgents.length > 0 && (
                                <>
                                    <span className="flex bg-gray-light py-2 px-4 font-bold">Agents</span>
                                    {filteredAgents.map((agent) => (
                                        <Checkbox
                                            key={agent.id}
                                            checked={selection.isSelected(agent)}
                                            // TODO: Fix in ui-kit. Throws a warning when `label` is not a string
                                            label={<AgentCheckboxContent agent={agent} />}
                                            className={clsx(
                                                "px-3 py-2 font-bold",
                                                selection.isSelected(agent)
                                                    ? "bg-primary-lighter"
                                                    : "odd:bg-gray-lighter",
                                            )}
                                            classNames={{ checkbox: "!border !border-gray" }}
                                            onChange={() => {
                                                selection.select(agent);
                                            }}
                                        />
                                    ))}
                                </>
                            )}
                            {shouldShowOperators && filteredOperators.length > 0 ? (
                                <>
                                    <span className="flex bg-gray-light py-2 px-4 font-bold">Operators</span>
                                    {filteredOperators.map((operator) => (
                                        <Checkbox
                                            key={operator.id}
                                            checked={operatorSelection.isSelected(operator)}
                                            // TODO: Fix in ui-kit. Throws a warning when `label` is not a string
                                            label={<OperatorCheckboxContent operator={operator} />}
                                            className={clsx(
                                                "px-3 py-2 font-bold",
                                                operatorSelection.isSelected(operator)
                                                    ? "bg-primary-lighter"
                                                    : "odd:bg-gray-lighter",
                                            )}
                                            classNames={{ checkbox: "!border !border-gray" }}
                                            onChange={() => operatorSelection.select(operator)}
                                        />
                                    ))}
                                </>
                            ) : null}
                        </InfiniteList>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                <SubmitButton
                    disabled={!selection.hasSelection && !operatorSelection.hasSelection}
                    onClick={handleConfirmClick}
                >
                    Apply
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

const isFilteredSelectionChecked = <T extends { id: string }>(
    filteredItems: T[],
    storedIds: string[] | undefined,
): boolean => {
    return storedIds ? filteredItems.every((item) => storedIds.includes(item.id)) : false;
};

const AgentCheckboxContent = ({ agent }: { agent: Affiliate }) => {
    return (
        <span className="flex items-center">
            <NoTranslate className="ml-2 font-bold text-black">{agent.name}</NoTranslate>
        </span>
    );
};

const OperatorCheckboxContent = ({ operator }: { operator: XolaPartner }) => {
    return (
        <span className="flex items-center">
            <NoTranslate className="ml-2 font-bold text-black">{operator.partnerSeller?.name}</NoTranslate>
        </span>
    );
};

const getSelectionCount = (
    agents: Affiliate[],
    operators: XolaPartner[],
    agentSelection: SelectionState,
    operatorSelection: SelectionState,
) => {
    if (agentSelection.all && operatorSelection.all) {
        return "All";
    }

    const selectedAgents = agentSelection.all ? agents.length : agentSelection.selected.length;
    const selectionOperators = operatorSelection.all ? operators.length : operatorSelection.selected.length;
    return selectedAgents + selectionOperators;
};

const getSelectedPartnerSellersFromPartners = (operators: XolaPartner[], selectedIds: ID[]) => {
    const sellers = [];
    for (const id of selectedIds) {
        const xolaPartner = operators.find((operator) => operator.id === id);
        if (xolaPartner) {
            sellers.push(xolaPartner.partnerSeller.id);
        }
    }

    return sellers;
};

const getPartnerStateFromPartnerSellerState = (
    operators: XolaPartner[],
    selectionState?: SelectionState,
): SelectionState => {
    if (!selectionState) {
        return { all: true, selected: [] };
    }

    const selectedXolaPartners = [];
    for (const id of selectionState.selected) {
        const xolaPartner = operators.find((operator) => operator.partnerSeller.id === id);
        if (xolaPartner) {
            selectedXolaPartners.push(xolaPartner.id);
        }
    }

    return {
        all: selectionState.all,
        selected: selectedXolaPartners,
    };
};
